import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import SkeletonGrid from '../../../../../components/SkeletonLoader/SkeletonGrid';
import '../styles/standardPrinting.css';
import CustomToast from '../../../../../components/Toast';

const StandardPrintingSidebar = ({
  questionDetails,
  updateQuestionDetails,
  setGotoId,
  isLoading,
  isTwoColumnStack,
  gotoThumbnailId,
  fixContentAlignment,
  autoScroll
}) => {
  const [items, setItems] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const [isOutsideSidebar, setIsOutsideSidebar] = useState(false);
  const sidebarContainerRef = useRef(null);
  const sidebarDraggedElementRef = useRef(null);
  const scrollThumbnailContainerRef = useRef(null);
  const isSidebarDraggingRef = useRef(false);
  const isOutsideSidebarRef = useRef(false);
  const invalidStateTimerRef = useRef(null);

  const setOutsideSidebarState = (value) => {
    if (isOutsideSidebarRef.current === value) return;
    isOutsideSidebarRef.current = value;
    setIsOutsideSidebar(value);
  };

  const clearInvalidStateTimer = () => {
    if (!invalidStateTimerRef.current) return;
    clearTimeout(invalidStateTimerRef.current);
    invalidStateTimerRef.current = null;
  };

  const onDragStart = (event, index) => {
    if (window.getSelection) {
      window.getSelection().removeAllRanges();
    }
    isSidebarDraggingRef.current = true;
    event.dataTransfer.setData('text/plain', index);
    setDraggedIndex(index);
    sidebarDraggedElementRef.current = event.currentTarget;
  };

  const onDragOver = (event, index) => {
    event.preventDefault();
    setDropIndex(index);
  };

  const onDrop = (event, index) => {
    event.preventDefault();
    const sidebarRef = sidebarContainerRef.current;
    const draggedRef = sidebarDraggedElementRef.current;

    if (!sidebarRef) return;
    const isDropInside = sidebarRef.contains(event.target);
    const isDraggedInside = draggedRef && sidebarRef.contains(draggedRef);

    if (!isDropInside || !isDraggedInside) {
      CustomToast('You are trying to drop content from outside sidebar.', 'warning');
      setDraggedIndex(null);
      setDropIndex(null);
      clearInvalidStateTimer();
      setOutsideSidebarState(false);
      sidebarDraggedElementRef.current = null;
      return;
    }

    const draggedItemIndex = parseInt(event.dataTransfer.getData('text/plain'));
    const hasAtLeastOnePosition = items?.some(q => q.position === 'left' || q.position === 'right');
    const hasAtLeastNoPosition = items?.some(q => !q.position);
    const isMixedType = hasAtLeastOnePosition && hasAtLeastNoPosition;
    let updatedItems = [];

    if (isTwoColumnStack) {
      const groupedStacks = [];
      let currentStack = [];
      let currentStackNo = items[0]?.stackNo;

      items.forEach((item) => {
        if (item.stackNo === currentStackNo) {
          currentStack.push(item);
        } else {
          groupedStacks.push(currentStack);
          currentStack = [item];
          currentStackNo = item.stackNo;
        }
      });
      if (currentStack.length > 0) groupedStacks.push(currentStack);

      const stackToMove = groupedStacks[draggedItemIndex];
      groupedStacks.splice(draggedItemIndex, 1);
      groupedStacks.splice(index, 0, stackToMove);

      let questionCounter = 1;
      const reorderedItems = groupedStacks.flatMap((stack, stackIdx) =>
        stack.map((q) => ({
          ...q,
          stackNo: stackIdx + 1,
          questionNo: questionCounter++,
        }))
      );

      setItems(reorderedItems);
      updateQuestionDetails(reorderedItems);
    } else if (isMixedType) {
      const tiles = [];
      let currentStackNo = null;
      let currentTile = [];

      items.forEach(q => {
        if (q.stackNo) {
          if (q.stackNo !== currentStackNo) {
            if (currentTile.length > 0) tiles.push(currentTile);
            currentTile = [q];
            currentStackNo = q.stackNo;
          } else {
            currentTile.push(q);
          }
        } else {
          if (currentTile.length > 0) {
            tiles.push(currentTile);
            currentTile = [];
          }
          tiles.push([q]);
          currentStackNo = null;
        }
      });
      if (currentTile.length > 0) tiles.push(currentTile);

      const draggedTile = tiles.splice(draggedIndex, 1)[0];
      tiles.splice(dropIndex, 0, draggedTile);

      let counter = 1;
      const reordered = tiles.flat().map(q => ({
        ...q,
        questionNo: counter++,
      }));

      setItems(reordered);
      updateQuestionDetails(reordered);
    } else {
      updatedItems = [...items];
      const itemToMove = updatedItems[draggedItemIndex];
      updatedItems.splice(draggedItemIndex, 1);
      updatedItems.splice(index, 0, itemToMove);

      const reorderedItems = updatedItems.map((q, i) => ({
        ...q,
        questionNo: i + 1,
      }));

      setItems(reorderedItems);
      updateQuestionDetails(reorderedItems);
    }

    setDraggedIndex(null);
    setDropIndex(null);
    clearInvalidStateTimer();
    setOutsideSidebarState(false);
    sidebarDraggedElementRef.current = null;
    document.body.style.cursor = 'default';
  };

  const onDragLeave = () => {
    // Keep drop state stable while crossing box edges; global dragover handles invalid zones.
  };

  const onDragEnd = () => {
    isSidebarDraggingRef.current = false;
    sidebarDraggedElementRef.current = null;
    setDraggedIndex(null);
    setDropIndex(null);
    clearInvalidStateTimer();
    setOutsideSidebarState(false);
    document.body.style.cursor = 'default';
  };

  const handleGoToButton = (id) => {
    setGotoId(id);
  };

  useEffect(() => {
    setItems(questionDetails);
  }, [questionDetails]);

  const generateFillInQuestion = (questionText, answers) => {
    let newQuestionText = questionText;
    if (answers) {
      answers.forEach((item, index) => {
        newQuestionText = newQuestionText.replace(`~~${index + 1}~~`, '<span class=\'answer-box\'></span>');
      });
    }
    return newQuestionText;
  };

  const RenderQuestion = (item, questionNo) => {
    return (
      <div key={`thumbnail-${item.questionId}`} id={`thumbnail-${item.questionId}`} className="element-wrapper text-black disable-Pointer-Events">
        <div className={`mb-2 ${item.layoutOptions?.includes('exclude') && 'bg-castleGray'}`}>
          {item.layoutOptions?.includes('exclude') ?
            <h7 className='fw-semibold'>Excluded</h7> : <h7 className='fw-semibold'>Q{questionNo}</h7>
          }
        </div>
        <div className={item.stackNo != null ? 'zoom-26' : 'zoom-5'}>
          {item.question?.resources?.map((resource, index) => (
            <div key={index} dangerouslySetInnerHTML={{ __html: fixContentAlignment(resource, item.typeId) }}></div>
          ))}
          {item.question != null && (
            <div dangerouslySetInnerHTML={{ __html: item.type == 'FillIn' ? generateFillInQuestion(fixContentAlignment(item.question?.question, item.typeId), item.question?.fillInAnswers) : fixContentAlignment(item.question?.question, item.typeId) }}></div>
          )}
          {item.blockedText != null && item.blockedText.length > 0 && (
            <div
              className='fw-bold text-danger'
              dangerouslySetInnerHTML={{ __html: item.blockedText }}></div>
          )}
          {item.type === 'MC' && item.question.mcAnswer.filter(a => a.answer.trim() !== '').length > 1 && (
            <div className='mcAnswer-data'>
              <ol className="answer-options">
                {item.question?.mcAnswer?.length > 0 &&
                item.question?.mcAnswer.map((answer, indexValue) => (
                  <li key={`ans${indexValue}`} dangerouslySetInnerHTML={{ __html: fixContentAlignment(answer.answer.replace(/<p>/g, '').replace(/<\/p>/g, ''), item.typeId) }}></li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (gotoThumbnailId !== null) {
      const selector = `#thumbnail-${gotoThumbnailId}`;
      const thumbnailElement = sidebarContainerRef.current.querySelector(selector);

      if (thumbnailElement) {
        thumbnailElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setGotoId(null);
      }
    }
  }, [gotoThumbnailId]);

  useEffect(() => {
    const handleDragOver = (e) => {
      if (!isSidebarDraggingRef.current) return;

      const scrollContainer = scrollThumbnailContainerRef.current;
      const sidebarContainer = sidebarContainerRef.current;
      if (!scrollContainer || !sidebarContainer) return;

      const rect = scrollContainer.getBoundingClientRect();
      const isInsideScrollPanel = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
      const wrappers = sidebarContainer.querySelectorAll('.question-wrapper');
      const edgeTolerance = 4;
      const isValidDropArea = Array.from(wrappers).some((wrapper) => {
        const wrapperRect = wrapper.getBoundingClientRect();
        return e.clientX >= wrapperRect.left &&
          e.clientX <= wrapperRect.right &&
          e.clientY >= wrapperRect.top - edgeTolerance &&
          e.clientY <= wrapperRect.bottom + edgeTolerance;
      });

      if (!isInsideScrollPanel || !isValidDropArea) {
        setDropIndex(null);
        if (!invalidStateTimerRef.current && !isOutsideSidebarRef.current) {
          invalidStateTimerRef.current = setTimeout(() => {
            invalidStateTimerRef.current = null;
            setOutsideSidebarState(true);
          }, 60);
        }
        document.body.style.cursor = 'not-allowed';
        return;
      }

      clearInvalidStateTimer();
      setOutsideSidebarState(false);
      document.body.style.cursor = 'default';
      autoScroll(e.clientY, scrollThumbnailContainerRef);
    };
  
    document.addEventListener('dragover', handleDragOver);
  
    return () => {
      document.removeEventListener('dragover', handleDragOver);
      clearInvalidStateTimer();
      setOutsideSidebarState(false);
      document.body.style.cursor = 'default';
    };
  }, []);

  return (
    <div className={`print-sidebar ${isOutsideSidebar ? 'drop-outside-active' : ''}`} ref={scrollThumbnailContainerRef}>
      {isOutsideSidebar && (
        <div className="invalid-drop-indicator" role="status" aria-live="polite">
          Cannot drop here. Move over a question box.
        </div>
      )}
      <div>
        {isLoading ?
          <SkeletonGrid
            rowCount={1}
            colCount={1}
            className='p-1 h-40 mt-3'
          />
          :
          <h4 className='mb-3'>
            {isTwoColumnStack ? <>Stacks<span> ({Math.max(...items.map(item => item.stackNo || 0), 0)})</span></> : <>Questions<span> ({items.length})</span></>}
          </h4>
        }
        <div ref={sidebarContainerRef} className="sidebar-content">
          {isLoading ? (
            <SkeletonGrid
              rowCount={4}
              colCount={1}
              className='p-1 h-250 mt-3'
            />
          ) : (
            <>
              <div className='thumbnail-container'>
                {(() => {
                  if (!items || items.length === 0) return null;

                  const renderedElements = [];
                  let leftColumn = [];
                  let rightColumn = [];
                  let currentStackNo = null;

                  items.forEach((item, index) => {
                    const hasPosition = item.position === 'left' || item.position === 'right';
                    const nextItem = items[index + 1];
                    const nextStackNo = nextItem?.stackNo;

                    if (hasPosition) {
                      if (item.stackNo) currentStackNo = item.stackNo;

                      if (item.position === 'left') leftColumn.push(item);
                      else rightColumn.push(item);

                      if (item.isEndStack) {
                        const stackIndex = renderedElements.length;
                        const stackNo = currentStackNo;

                        renderedElements.push(
                          <div
                            id={`stack_${stackNo ?? stackIndex}`}
                            key={`stack_${stackNo ?? stackIndex}`}
                            draggable
                            onDragStart={(e) => onDragStart(e, stackIndex)}
                            onDragOver={(e) => onDragOver(e, stackIndex)}
                            onDrop={(e) => onDrop(e, stackIndex)}
                            onDragLeave={onDragLeave}
                            onDragEnd={onDragEnd}
                            className={`d-flex row mb-3 question-wrapper cursor-grab p-2 no-select ${draggedIndex === stackIndex ? 'dragged' : ''}`}
                          >
                            <div className={`thumbnail-element p-0 ${sidebarDraggedElementRef.current ? 'overflow-hidden' : 'overflow-y'} ${dropIndex == stackIndex ? 'disable-Pointer-Events' : ''}`}>
                              <h5>Stack</h5>
                              <div className="d-flex gap-2">
                                <div style={{ width: '47%' }}>
                                  {leftColumn.map((q) => (
                                    <div key={q.questionId} className="stack_question mb-2">
                                      {RenderQuestion(q, q.questionNo)}
                                    </div>
                                  ))}
                                </div>
                                <div style={{ width: '47%' }}>
                                  {rightColumn.map((q) => (
                                    <div key={q.questionId} className="stack_question mb-2">
                                      {RenderQuestion(q, q.questionNo)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                            {dropIndex === stackIndex && sidebarDraggedElementRef.current ?
                              <div className="drop-placeholder">Drop here</div>
                              :
                              <button className="goto-btn" onClick={() => handleGoToButton(stackNo ?? stackIndex)}>
                                Go To <FontAwesomeIcon icon={faArrowRight} />
                              </button>
                            }
                          </div>
                        );

                        leftColumn = [];
                        rightColumn = [];
                        currentStackNo = nextStackNo ?? null;
                      }
                    } else {
                      const singleIndex = renderedElements.length;

                      renderedElements.push(
                        <div
                          key={`single_${item.questionId}`}
                          draggable
                          onDragStart={(e) => onDragStart(e, singleIndex)}
                          onDragOver={(e) => onDragOver(e, singleIndex)}
                          onDrop={(e) => onDrop(e, singleIndex)}
                          onDragLeave={onDragLeave}
                          onDragEnd={onDragEnd}
                          className={`question-wrapper p-2 row mb-3 cursor-grab no-select ${draggedIndex === singleIndex ? 'dragged' : ''}`}
                        >
                          <div className={`thumbnail-element ${dropIndex == singleIndex ? 'disable-Pointer-Events' : ''}`}>
                            {RenderQuestion(item, item.questionNo)}
                          </div>
                          {dropIndex === singleIndex && sidebarDraggedElementRef.current ?
                            <div className="drop-placeholder">Drop here</div>
                            : 
                            <button className="goto-btn" onClick={() => handleGoToButton(item.questionId)}>
                              Go To <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                          }
                        </div>
                      );
                    }
                  });

                  return renderedElements;
                })()}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

StandardPrintingSidebar.propTypes = {
  questionDetails: PropTypes.array,
  updateQuestionDetails: PropTypes.func,
  setGotoId: PropTypes.func,
  isLoading: PropTypes.bool,
  isTwoColumnStack: PropTypes.bool,
  gotoThumbnailId: PropTypes.number,
  fixContentAlignment: PropTypes.func,
  autoScroll: PropTypes.func
};

export default StandardPrintingSidebar;
